import { RefObject } from 'react';
import axios, { AxiosRequestConfig, AxiosResponse, AxiosPromise } from 'axios';

import { getLocalStorageItem, logError } from 'utils/helpers';
import { API_ROOT, STORE_ROOT, CONTENTFUL_ROOT } from 'utils/constants/urls';
import { LineItem, AxiosErrorResponse } from 'utils/types';

const COVID_ORDER_KEY = 'current_covid_order';
const ORDER_KEY = 'current_order';

import Cookies from 'js-cookie';

let onCartTrigger: CustomEvent | undefined;

import { showFlash } from 'utils/domHelpers';

if (typeof window !== 'undefined') {
  onCartTrigger = new CustomEvent('onCartTrigger');
}

/**
 * Handle axios error responses.
 * These will return either an array of strings, or just a string.
 * Always return an array for clean UI handling.
 */
const getResponseErrors = (err: AxiosErrorResponse): string[] | undefined => {
  const error = err.response && err.response.data && err.response.data.errors;
  if (typeof error === 'string') {
    return [error];
  }

  if (Array.isArray(error)) {
    return error;
  }

  if (error === Object(error)) {
    return Object.keys(error).map(key => {
      const errorStr = Array.isArray(error[key])
        ? // @ts-ignore
          error[key].join(',')
        : error[key];

      // Not using this for now, but could in the future.
      // This is for prop: errorMsg format. Instead for now we'll use just errorMsg
      // -Jeron 10/18/20
      // const capKey = key.charAt(0).toUpperCase() + key.slice(1);
      // return `${capKey}: ${errorStr}`;

      return errorStr;
    });
  }

  return undefined;
};

const getStore = (): any => {
  // there is no localStorage at build time so return empty object instead
  if (typeof localStorage === 'undefined') return {};

  const cookie = Cookies.get('customStorage');

  const store =
    cookie && cookie.length === 0
      ? cookie
      : getLocalStorageItem('customStorage');

  if (!store) localStorage.setItem('customStorage', '{}');

  return store ? JSON.parse(store) : {};
};

/**
 * Entire order data including line items, discount, subtotal, promotions
 */
const getCurrentOrder = (): any => {
  const storeRaw = getStore();
  const { current_order } = storeRaw;
  return current_order ? JSON.parse(current_order) : {};
};

const getLineItems = () => {
  const currentOrderRaw = getCurrentOrder();
  const { line_items } = currentOrderRaw;
  // return line_items ? JSON.parse(line_items) : [];
  return line_items || [];
};

const getCartCount = (): number =>
  getLineItems().reduce(
    (acc: number, item: { quantity: number }) => acc + item.quantity,
    0,
  );

const getSession = (): AxiosPromise<any> | undefined => {
  if (typeof window !== 'undefined') {
    const requestOpts: AxiosRequestConfig = {
      method: 'GET',
      url: `${
        // @ts-ignore
        window.storeRoot || STORE_ROOT
      }/sessions/status.json?location=${window.location}`,
      withCredentials: true,
    };

    return axios(requestOpts);
  }
};

const addDataToStore = (data: {
  [prop: string]: boolean | number | string;
}): void => {
  const store = getStore();

  Object.keys(data).forEach((prop: string) => {
    store[prop] = data[prop];
  });

  localStorage.setItem('customStorage', JSON.stringify(store));
};

function parseJwt(token: string) {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join(''),
  );

  return JSON.parse(jsonPayload);
}

const isAuthTokenExpired = (token: string) => {
  if (!token) return true;
  const parsedJwt = parseJwt(token);
  // returned in seconds
  if (parsedJwt.exp) {
    const now = new Date().getTime();
    const nowInSeconds = now / 1000;
    const expInSeconds = parsedJwt.exp;
    if (nowInSeconds > expInSeconds) {
      return true;
    } else {
      return false;
    }
  } else {
    return true;
  }
};

const getAuthToken = async () => {
  const store = getStore();

  const authToken = store.auth_token;
  const isLoggedIn = store.logged_in;

  // if user is logged in and has authToken, return that token even if expired
  if (authToken && isLoggedIn) {
    return authToken;
  }

  const authTokenExpired = isAuthTokenExpired(authToken);

  // if the authToken hasn't expired and user is not logged in, return it
  if (authToken && !authTokenExpired) {
    return authToken;
  }

  try {
    deleteOrderFromStore();
    const sessionResponse = await getSession();
    sessionResponse && addDataToStore(sessionResponse.data);
    // store will update with session, so we need to retrieve it again
    return getStore().auth_token;
  } catch (error) {
    logError('error in getAuthToken', {
      component: 'cartHelpers',
      function: 'getAuthToken',
      stackTrace: error,
    });
    throw error;
  }
};

const initOrderData = (): any => {
  const store = getStore();

  if (store.logged_in) {
    return { user_id: store.user_id };
  }

  return { guest_token: store.guest_token };
};

const createOrder = async (): Promise<any> => {
  if (typeof window !== 'undefined') {
    try {
      const authToken = await getAuthToken();
      const requestOpts: AxiosRequestConfig = {
        data: initOrderData(),
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        method: 'POST',
        url: `${
          // @ts-ignore
          window.apiRoot || API_ROOT
        }/v2/orders/?location=${window.location}`,
      };
      return axios(requestOpts);
    } catch (error) {
      resetSession();

      const errors = getResponseErrors(error);
      if (errors) {
        const event = new CustomEvent('onCartTrigger', {
          detail: { error: { type: 'cartError', errors } },
        });

        window.dispatchEvent(event);
      }
      return Promise.resolve();
    }
  }
};

const saveOrder = (order: AxiosResponse, key: string = ORDER_KEY): void => {
  const store = getStore();

  store[key] = JSON.stringify(order.data);
  localStorage.setItem('customStorage', JSON.stringify(store));
  Cookies.set('customStorage', JSON.stringify(store));
};

const getOrder = async (key: string = ORDER_KEY): Promise<string> => {
  const order = getStore()[key];
  if (order) return Promise.resolve(order);

  try {
    // no order was found, so we're making a new one
    const orderResponse = await createOrder();
    orderResponse && saveOrder(orderResponse);

    // store will update with order, so we need to retrieve it again
    return Promise.resolve(getStore()[key]);
  } catch (error) {
    resetSession();

    const errors = getResponseErrors(error);
    if (errors) {
      const event = new CustomEvent('onCartTrigger', {
        detail: { error: { type: 'cartError', errors } },
      });

      window.dispatchEvent(event);
    }
    return Promise.resolve(`error in getOrder: ${error}`);
  }
};

// TODO: need to add new error handling here too?
const updateOrderAndGoToCart = async (retry?: boolean): Promise<any> => {
  if (typeof window !== 'undefined') {
    let orderUrl: string;
    try {
      const orderId = JSON.parse(await getOrder()).id;
      const authToken = await getAuthToken();
      orderUrl = `${
        // @ts-ignore
        window.apiRoot || API_ROOT
      }/v2/orders/${orderId}`;

      const requestOpts: AxiosRequestConfig = {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        method: 'GET',
        url: orderUrl,
      };

      await axios(requestOpts)
        .then((resp: AxiosResponse) => {
          saveOrder(resp);
        })
        .then(() => {
          if (onCartTrigger) {
            window.dispatchEvent(onCartTrigger);
          }
        })
        .catch(error => {
          // if updating order errors, reset session and try again
          if (orderUrl === error.config?.url && !retry) {
            const errors = getResponseErrors(error);
            if (errors) {
              const event = new CustomEvent('onCartTrigger', {
                detail: { error: { type: 'cartError', errors } },
              });

              window.dispatchEvent(event);
            }
            resetSession();
            updateOrderAndGoToCart(true);
          }
        });
    } catch (error) {
      const errors = getResponseErrors(error);
      if (errors) {
        const event = new CustomEvent('onCartTrigger', {
          detail: { error: { type: 'cartError', errors } },
        });

        window.dispatchEvent(event);
      }
    }
  }
};

const resetSession = (): void => {
  localStorage.setItem('customStorage', '{}');
  Cookies.set('customStorage', '{}');
};

const addLineItem = async (data: LineItem, retry?: boolean): Promise<void> => {
  if (typeof window !== 'undefined') {
    let lineItemUrl;

    try {
      const orderRaw = await getOrder();
      const myOrder = JSON.parse(orderRaw);
      const orderId = myOrder.id;
      const authToken = await getAuthToken();

      // If local, set apiRoot to http://localhost:3000/aapi
      lineItemUrl = `${
        // @ts-ignore
        window.apiRoot || API_ROOT
      }/v1/orders/${orderId}/line_items?location=${window.location}`;

      const requestOpts: AxiosRequestConfig = {
        data,
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        method: 'POST',
        url: lineItemUrl,
      };

      await axios(requestOpts);
      return updateOrderAndGoToCart();
    } catch (error) {
      const errors = getResponseErrors(error);

      if (errors) {
        const event = new CustomEvent('onCartTrigger', {
          detail: { error: { type: 'cartError', errors } },
        });

        window.dispatchEvent(event);
      }

      if (
        lineItemUrl === error.config?.url &&
        !retry &&
        error.response?.status === 401
      ) {
        resetSession();
      }

      if (error.response?.status === 404) {
        deleteOrderFromStore(true);
      }

      return Promise.reject(errors);
    }
  }
};

const deleteOrderFromStore = (closeCartAndShowError?: boolean): void => {
  const store = getStore();
  delete store.current_order;
  localStorage.setItem('customStorage', JSON.stringify(store));
  Cookies.set('customStorage', JSON.stringify(store));

  if (closeCartAndShowError) {
    showFlash(
      'alert-error',
      'Failed to locate your previous order. Sorry for the inconvenience!',
    );
    setCartOpen(false);
  }
};

// interacts with the CartContainer to open/close the cart pullout
const setCartOpen = (open?: boolean): void => {
  const event = new CustomEvent('onSetCartOpen', {
    detail: { open },
  });
  window.dispatchEvent(event);
};

const editLineItem = async (item: LineItem, retry?: boolean): Promise<void> => {
  if (typeof window !== 'undefined') {
    let lineItemUrl;
    try {
      lineItemUrl = `${
        // @ts-ignore
        window.apiRoot || API_ROOT
      }/v1/orders/${item.order_id}/line_items?location=${window.location}`;
      const authToken = await getAuthToken();

      const itemData = {
        variant_id: item.variant_id,
        quantity: item.quantity,
        total_quantity: item.quantity,
      };

      const requestOpts: AxiosRequestConfig = {
        data: itemData,
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        method: 'POST',
        url: lineItemUrl,
      };

      await axios(requestOpts);
      updateOrderAndGoToCart();
    } catch (error) {
      const errors = getResponseErrors(error);

      if (errors) {
        const event = new CustomEvent('onCartTrigger', {
          detail: { error: { type: 'cartError', errors } },
        });

        window.dispatchEvent(event);
      }

      if (lineItemUrl === error.config?.url && !retry) {
        resetSession();
      }

      if (error.response?.status === 404) {
        deleteOrderFromStore(true);
      }
    }
  }
};

// TODO: finish line item removal
const removeLineItem = async (
  item: LineItem,
  retry?: boolean,
): Promise<void> => {
  if (typeof window !== 'undefined') {
    const orderId = item.order_id || getStore().current_order;
    let lineItemUrl: string | null = null;
    try {
      lineItemUrl = `${
        // @ts-ignore
        window.apiRoot || API_ROOT
      }/v1/orders/${orderId}/line_items/${item.id}?location=${window.location}`;
      const authToken = await getAuthToken();

      const requestOpts: AxiosRequestConfig = {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        method: 'DELETE',
        url: lineItemUrl,
      };

      await axios(requestOpts);
      updateOrderAndGoToCart();
    } catch (error) {
      const errors = getResponseErrors(error);

      if (errors) {
        const event = new CustomEvent('onCartTrigger', {
          detail: { error: { type: 'cartError', errors } },
        });

        window.dispatchEvent(event);
      }

      if (lineItemUrl === error.config?.url && !retry) {
        resetSession();
      }

      if (error.response?.status === 404) {
        deleteOrderFromStore(true);
      }
    }
  }
};

// TODO finish retrieving the product for removal
const removeProduct = async (variantId: number) => {
  const lineItems = getLineItems();
  const index = lineItems.findIndex(
    (p: LineItem) => p.variant_id === variantId,
  );

  if (index >= 0) {
    await removeLineItem(lineItems[index]);
  }
};

const addToCart = async (
  variantId: number,
  button?: RefObject<HTMLDivElement>,
  callback?: () => void,
  quantity?: number,
): Promise<void> => {
  if (button?.current?.getAttribute('disabled')) return;
  button?.current?.setAttribute('disabled', 't');

  const data = {
    quantity: quantity || 1,
    variant_id: variantId,
  };

  addLineItem(data).then(() => {
    callback && callback();
    button?.current?.removeAttribute('disabled');
  });
};

const addMembershipToOrder = async (variantId: number): Promise<void> => {
  const data = {
    quantity: 1,
    variant_id: variantId,
  };

  return addLineItem(data);
};

const redeemCredit = async (data: any) => {
  let creditRedemptionUrl;
  try {
    const authToken = await getAuthToken();
    creditRedemptionUrl = API_ROOT + '/v1/credits/redeem';

    const requestOpts: AxiosRequestConfig = {
      data: data,
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      method: 'POST',
      url: creditRedemptionUrl,
    };

    const result = await axios(requestOpts);
    return result;
  } catch (error) {
    logError('there was an issue redeeming this credit', {
      component: 'cartHelpers',
      function: 'redeemCredit',
      stackTrace: error,
    });
    return {};
  }
};

const orderMembershipKit = async (
  button: RefObject<HTMLDivElement>,
  variantId: number,
  userId: number,
  callback: (data: any) => void,
): Promise<void> => {
  if (!button.current || button.current.getAttribute('disabled')) return;

  button.current.setAttribute('disabled', 't');

  const redeemParams = { user_id: userId, variant_id: variantId };

  redeemCredit(redeemParams)
    .then(res => {
      // @ts-ignore
      const { data } = res;
      if (callback) {
        callback(data);
      }
      if (button.current) {
        button.current.removeAttribute('disabled');
      }
    })
    .catch(err =>
      logError('there was an issue redeeming credit', {
        component: 'cartHelpers',
        function: 'orderMembershipKit',
        stackTrace: err,
      }),
    );
};

const getUser = async () => {
  try {
    const sessionResponse = await getSession();
    sessionResponse && addDataToStore(sessionResponse.data);
    return sessionResponse;
  } catch (error) {
    logError('there was an issue getting user session data', {
      component: 'cartHelpers',
      function: 'getUser',
      stackTrace: error,
    });
    throw error;
  }
};

const getLineItemImage = async (productId: number, callback: Function) => {
  if (typeof window !== 'undefined') {
    const requestOpts: AxiosRequestConfig = {
      method: 'GET',
      url: `${CONTENTFUL_ROOT}/spaces/${
        // @ts-ignore
        window.contentfulSpaceKey ||
          process.env.CONTENTFUL_PRODUCTS_SPACE_ID ||
          process.env.CONTENTFUL_SPACE_KEY
      }/environments/${
        // @ts-ignore
        window.contentfulEnv || process.env.CONTENTFUL_ENV
      }/entries?access_token=${
        // @ts-ignore
        window.contentfulAccessToken ||
          process.env.CONTENTFUL_PRODUCTS_ACCESS_TOKEN ||
          process.env.CONTENTFUL_ACCESS_TOKEN
      }&content_type=product&fields.productId=${productId}&select=fields.pdpImage`,
    };

    axios(requestOpts)
      .then((resp: AxiosResponse) => {
        callback(resp.data.includes.Asset[0].fields.file.url);
      })
      .catch(error => {
        logError('there was an issue fetching the kit image', {
          component: 'cartHelpers',
          function: 'getLineItemImage',
          stackTrace: error,
        });
      });
  }
};

const addPromoToOrder = async (orderId: number, promoCode: string) => {
  if (typeof window !== 'undefined') {
    try {
      const lineItemUrl = `${
        // @ts-ignore
        window.apiRoot || API_ROOT
      }/v2/orders/${orderId}/add_promo`;
      const authToken = await getAuthToken();

      const requestOpts: AxiosRequestConfig = {
        data: { coupon_code: promoCode },
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        method: 'PUT',
        url: lineItemUrl,
      };

      await axios(requestOpts);
      updateOrderAndGoToCart();
    } catch (error) {
      const errors = getResponseErrors(error);

      if (error.response?.status === 404) {
        deleteOrderFromStore(true);
      }

      if (errors) {
        const event = new CustomEvent('onCartTrigger', {
          detail: { error: { type: 'promoError', errors } },
        });

        window.dispatchEvent(event);
      }
    }
  }
};

const removePromos = async (orderId: number) => {
  if (typeof window !== 'undefined') {
    try {
      const lineItemUrl = `${
        // @ts-ignore
        window.apiRoot || API_ROOT
      }/v2/orders/${orderId}/remove_promos`;
      const authToken = await getAuthToken();

      const requestOpts: AxiosRequestConfig = {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        method: 'PUT',
        url: lineItemUrl,
      };

      await axios(requestOpts);
      updateOrderAndGoToCart();
    } catch (error) {
      if (error.response?.status === 404) {
        deleteOrderFromStore(true);
      }

      const errors = getResponseErrors(error);

      if (errors) {
        const event = new CustomEvent('onCartTrigger', {
          detail: { error: { type: 'promoError', errors } },
        });

        window.dispatchEvent(event);
      }
    }
  }
};

const formatPrice = (x: number) => {
  if (x) return x.toFixed(2);
  return '0.00';
};

const currentOrderGrandTotalInCents = (currentOrderGrandTotal: number) =>
  Math.round(parseFloat(formatPrice(currentOrderGrandTotal)) * 100);

const addToCartDataLayer = (
  productId: number,
  name: string,
  price: string,
): void => {
  window &&
    window.dataLayer.push({
      event: 'addToCart',
      ecommerce: {
        currencyCode: 'USD',
        add: {
          products: [
            {
              id: productId,
              name: name,
              price: price,
              is_subscription: 'false',
              brand: '',
              category: '',
              variant: '',
              quantity: 1,
            },
          ],
        },
      },
    });
};

const setExistingOrder = async (userid: string) => {
  try {
    const orderUrl = `${
      // @ts-ignore
      window.apiRoot || API_ROOT
    }/v1/users/${userid}/current_order`;
    const authToken = await getAuthToken();

    const requestOpts: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      method: 'GET',
      url: orderUrl,
    };

    await axios(requestOpts)
      .then((resp: AxiosResponse) => {
        saveOrder(resp);
      })
      .then(() => {
        if (typeof window !== 'undefined' && onCartTrigger) {
          window.dispatchEvent(onCartTrigger);
        }
      });
  } catch (error) {
    const errors = getResponseErrors(error);

    if (errors) {
      const event = new CustomEvent('onCartTrigger', {
        detail: { error: { type: 'promoError', errors } },
      });

      window.dispatchEvent(event);
    }
  }
};

const updateFailedOrderGuestToken = async (orderId: string, token: string) => {
  if (typeof window !== 'undefined') {
    try {
      const lineItemUrl = `${
        // @ts-ignore
        window.apiRoot || API_ROOT
      }/v2/orders/${orderId}/token_update?registration_token=${token}`;
      const authToken = await getAuthToken();

      const requestOpts: AxiosRequestConfig = {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        method: 'PUT',
        url: lineItemUrl,
      };

      await axios(requestOpts)
        .then((resp: AxiosResponse) => {
          saveOrder(resp);
        })
        .then(() => {
          if (typeof window !== 'undefined' && onCartTrigger) {
            window.dispatchEvent(onCartTrigger);
          }
        });
    } catch (error) {
      const errors = getResponseErrors(error);

      if (errors) {
        const event = new CustomEvent('onCartTrigger', {
          detail: { error: { type: 'promoError', errors } },
        });

        window.dispatchEvent(event);
      }
    }
  }
};

const syncCurrentOrderWithFailedOrder = (searchParams: any) => {
  updateFailedOrderGuestToken(
    searchParams.orderId,
    searchParams.registration_token,
  );

  /**
   * In the old experience, we had the following code that would
   * fill in the address fields on the form with the data from the
   * API. We need to replicate that functionality here.
   *
   * fillAddressFromApi();
   * $('.order-email').val(getOrderEmail());
   * $('#main-content-panel').removeClass('loading');
   */
};

function initCovidOrderData() {
  const store = getStore();
  if (store['logged_in']) {
    return { user_id: store['user_id'] };
  } else {
    return { guest_token: store['guest_token'] };
  }
}

function redirectToCovidCheckout() {
  window.location.href = '/covid-checkout';
}

const createCovidOrder = async (quantity = 1) => {
  if (typeof window !== 'undefined') {
    const url = STORE_ROOT + '/aapi/v2/orders';
    const authToken = await getAuthToken();
    const requestOpts: AxiosRequestConfig = {
      data: initCovidOrderData(),
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      method: 'POST',
      url,
    };

    try {
      const response = await axios(requestOpts);
      saveOrder(response, COVID_ORDER_KEY);

      let covidOrder = await getOrder(COVID_ORDER_KEY);
      covidOrder = JSON.parse(covidOrder);

      await createCovidLineItem(covidOrder, quantity);

      setTimeout(() => {
        redirectToCovidCheckout();
      }, 800);
    } catch (err) {
      console.log({ err });
      console.log({ requestOpts });
    }
  }
};

const createCovidLineItem = async (order: any, quantity: number) => {
  const authToken = await getAuthToken();
  const covidLineCreateUrl =
    STORE_ROOT + '/aapi/v1/orders/' + order.id + '/line_items/covid_create';
  const covidLineCreateRequestOpts: AxiosRequestConfig = {
    data: { quantity },
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
    method: 'POST',
    url: covidLineCreateUrl,
  };

  return await axios(covidLineCreateRequestOpts);
};

export {
  addToCart,
  addLineItem,
  editLineItem,
  removeLineItem,
  removeProduct,
  getLineItemImage,
  getCartCount,
  getCurrentOrder,
  getLineItems,
  formatPrice,
  addPromoToOrder,
  removePromos,
  getAuthToken,
  getOrder,
  orderMembershipKit,
  getUser,
  getResponseErrors,
  addMembershipToOrder,
  currentOrderGrandTotalInCents,
  addToCartDataLayer,
  resetSession,
  createCovidOrder,
};
